import type {
  CompanyType,
  ProductType,
  MarketType,
  ProjectStatus,
  TargetRegion,
} from '@/lib/api/client'

export const COMPANY_TYPE_LABELS: Record<CompanyType, string> = {
  concept: 'Concept',
  startup: 'Start-up',
  scale_up: 'Scale-up',
  grown_up: 'Grown-up',
  enterprise: 'Enterprise',
}

export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  software: 'Software product',
  hardware: 'Hardware product',
  service: 'Service',
  solution: 'Solution',
  platform: 'Platform',
  marketplace: 'Marketplace',
}

export const MARKET_LABELS: Record<MarketType, string> = {
  b2c: 'B2C',
  b2b: 'B2B',
  b2b2c: 'B2B2C',
  b2g: 'B2G',
  d2c: 'D2C',
}

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  archived: 'Archived',
}

// Stage tint maps to the M-B-S domain colors so the colorization is semantic,
// not decorative. Concept = exploring (Market teal), Start-up = decisive
// (Skerpa orange), Scale-up = building (Business blue), Grown-up = mature
// (Solution gold), Enterprise = ink.
export const COMPANY_TYPE_TINT: Record<CompanyType, string> = {
  concept: 'text-market-700',
  startup: 'text-accent-600',
  scale_up: 'text-business-700',
  grown_up: 'text-solution-700',
  enterprise: 'text-text',
}

// Dot background colors matching stage tints — used for the small indicator
// dot rendered before project names in cards and the header.
export const COMPANY_TYPE_DOT: Record<CompanyType, string> = {
  concept: 'bg-market-500',
  startup: 'bg-accent-500',
  scale_up: 'bg-business-500',
  grown_up: 'bg-solution-400',
  enterprise: 'bg-warm-600',
}

export const COMPANY_TYPE_OPTIONS: CompanyType[] = ['concept', 'startup', 'scale_up', 'grown_up', 'enterprise']
export const PRODUCT_TYPE_OPTIONS: ProductType[] = ['software', 'hardware', 'service', 'solution', 'platform', 'marketplace']
export const MARKET_OPTIONS: MarketType[] = ['b2c', 'b2b', 'b2b2c', 'b2g', 'd2c']
export const PROJECT_STATUS_OPTIONS: ProjectStatus[] = ['active', 'paused', 'archived']
export const TARGET_REGION_OPTIONS: TargetRegion[] = ['global', 'eu', 'benelux']
export const TARGET_REGION_LABELS: Record<TargetRegion, string> = {
  global: 'Global',
  eu: 'European Union',
  benelux: 'Benelux',
}

export function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'No activity yet'
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return 'No activity yet'
  const diff = Date.now() - then
  const minute = 60_000
  const hour = 60 * minute
  const day = 24 * hour
  const week = 7 * day
  if (diff < hour) {
    const m = Math.max(1, Math.round(diff / minute))
    return `Active ${m}m ago`
  }
  if (diff < day) {
    const h = Math.round(diff / hour)
    return `Active ${h}h ago`
  }
  if (diff < week) {
    const d = Math.round(diff / day)
    return `Active ${d}d ago`
  }
  const w = Math.round(diff / week)
  return `Active ${w}w ago`
}
